import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";

import { Eye, EyeClosed } from "@assets";
import { Button } from "@components/Button";
import { TextInput } from "@components/Input";
import { OnboardingLayout, OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Routes } from "@ctypes";
import { useErrorBanner, useResetPassword } from "@hooks";
import { i18n, Translations } from "@i18n";
import { constants } from "@layout";
import { PasswordValidator } from "@routes/SignUpRoute/PasswordValidator";
import { getQueryParams } from "@utils";
import { validationSchema } from "./content";
import { ResetPasswordFormValues } from "./types";

export const ResetPasswordRoute = () => {
  const [showValidator, setShowValidator] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const location = useLocation();

  const params = getQueryParams(location.search);

  const token = params.token as string | undefined;

  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
  } = useForm<ResetPasswordFormValues>({
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.ResetPasswordRouteTitle),
    subtitle: i18n.t(Translations.ResetPasswordRouteSubtitle),
  };

  const [resetPassword, { loading }] = useResetPassword();

  const { showErrorBanner } = useErrorBanner();
  const navigate = useNavigate();

  const newPasswordController = register("newPassword");
  const confirmPasswordController = register("confirmPassword");

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async ({ newPassword }: ResetPasswordFormValues) => {
    if (!token) return showErrorBanner();

    try {
      await resetPassword({
        variables: {
          password: newPassword,
          token,
        },
      });

      navigate(Routes.ResetPasswordSuccess, { replace: true });
    } catch {
      showErrorBanner();
    }
  };

  const disabled = !isValid || watch("newPassword") !== watch("confirmPassword");

  return (
    <OnboardingLayout {...props}>
      <TextInput
        {...newPasswordController}
        label={i18n.t(Translations.ResetPasswordRouteNewPasswordLabel)}
        value={watch("newPassword")}
        onChangeText={(text) => setValue("newPassword", text, constants.form)}
        type={showPassword ? "text" : "password"}
        error={errors.newPassword?.message}
        rightIcon={showPassword ? <EyeClosed /> : <Eye />}
        onRightIconClick={toggleShowPassword}
        placeholder={i18n.t(Translations.ResetPasswordRouteNewPasswordPlaceholder)}
        onFocusCallback={() => setShowValidator(true)}
        onBlurCallback={() => setShowValidator(false)}
      />
      <Spacing size={SpacingSizes.M} />

      <TextInput
        {...confirmPasswordController}
        label={i18n.t(Translations.ResetPasswordRouteConfirmPasswordLabel)}
        value={watch("confirmPassword")}
        onChangeText={(text) => setValue("confirmPassword", text, constants.form)}
        type={showPassword ? "text" : "password"}
        error={errors.confirmPassword?.message}
        rightIcon={showPassword ? <EyeClosed /> : <Eye />}
        onRightIconClick={toggleShowPassword}
        placeholder={i18n.t(Translations.ResetPasswordRouteConfirmPasswordPlaceholder)}
      />
      <Spacing size={SpacingSizes.M} />

      {showValidator || watch("newPassword").length > 0 ? (
        <PasswordValidator
          password={watch("newPassword")}
          confirmPassword={watch("confirmPassword")}
        />
      ) : null}

      <Button
        title={i18n.t(Translations.ResetPasswordRoutePrimaryCTA)}
        onClick={handleFormSubmit(handleSubmit)}
        disabled={disabled}
        loading={loading}
      />
      <Spacing size={SpacingSizes.L} />
    </OnboardingLayout>
  );
};
